// **The rounds-mode chat channel** (beekeeper-agent-chat, CONTRACT.md §7; `client-beekeeper-chat`).
//
// The stateful glue between `net/chat-sse.ts` (pure transport) and `ui/chat/transcript-model.ts`
// (pure reducer) — the rounds analogue of `rounds-prompt.ts`'s `RoundsPromptChannel`, and built the
// same way: an external-store object (`getSnapshot`/`subscribe`, wired into React through
// `useSyncExternalStore`) rather than a slice on the shared `useClientStore`, because a chat
// transcript's mid-turn state (an accreting text delta, a running tool row) has nothing else in the
// store that needs it, and keeping it here means `RoundsController` is the only thing that owns it —
// exactly like the run/review seams before it.
//
// Four behaviours, in the order the loop needs them:
//
//   1. **Send-and-open** (`PromptBox`'s entry point, 7.3/7.4): posts the message, opens the
//      overlay, and starts the turn's live event stream — the player's own line appears in the
//      transcript immediately, before the first server event has even arrived.
//   2. **Reopen without sending** (the separate re-entry control): opens the overlay over whatever
//      transcript state is already held, dispatching nothing.
//   3. **Stop**, **Minimise** and **Close**, thin wrappers — `stop()` posts to the stop route (the
//      transcript's own `turnFinished` event, not this call's resolution, is what actually ends
//      `running`); `close()` refuses while a turn is running, the same modality guarantee
//      `ChatOverlay` itself enforces, so a caller cannot dismiss a live turn by going around the
//      component; `minimise()` (`player-chat-and-outcome` **D-2**) is the ✕'s real destination and
//      is unconditional — it changes `view` and nothing else, so a turn behind a put-away panel
//      keeps running, keeps streaming and keeps writing into the transcript. That is the whole
//      point of the state: `closed` may tear down, `minimised` may not.
//   4. **Rehydrate**, called once at boot: fetches the thread, seeds the transcript from it, and —
//      the load-bearing case (7.15/7.16) — if it reports a turn in flight, opens modally and
//      re-attaches the live stream **from the start of that turn's log**, so a reload mid-turn
//      never shows a transcript that looks finished while the Beekeeper is still writing it, and
//      never one with a hole in it either: the thread body materializes a turn's agent output only
//      once the turn settles, so everything already emitted lives solely in the log and has to be
//      replayed. `transcriptFromThread` leaves the in-flight turn out of the cursor guard for
//      exactly this reason, which is what makes the `?? 0` below the whole log rather than a
//      suffix; the guard still makes the replay idempotent.
//
// A dropped connection (`onError` mid-turn) is recovered the same way a reload is: reattach from
// the highest cursor already applied, after a short backoff so a persistently offline transport
// does not spin. The reducer's own idempotent-replay guard (`transcript-model.ts`) is what makes a
// reattach safe to retry — a re-delivered event is a no-op, never a duplicate.
//
// **Closing the loop.** A `turnFinished` carrying a non-null `version` means the sandbox's document
// moved — the same signal `rounds-prompt.ts`'s status watch already acts on for the old one-shot
// path (its "closing the loop" note). Without re-reading the colony here, the accepted edit would
// be live on the server and invisible in the editor, which is the one thing a player watches the
// Beekeeper turn *for* — so this channel re-fetches and republishes `colonyMeta` the same way.

import type { ColonyMeta } from '../../net/wire/messages';
import type { ThreadDto, TurnStreamError, TurnStreamHandlers } from '../../net/chat-sse';
import type { Scheduler } from '../../net/wire/connection';
import { isChatBusy, type ChatPhase, type ChatSeam, type ChatSeamSnapshot } from '../../ui/colony/chat-seam';
import type { TranscriptState } from '../../ui/chat/transcript-model';
import {
  EMPTY_TRANSCRIPT,
  appendPlayerMessage,
  applyTurnEvent,
  transcriptFromThread,
} from '../../ui/chat/transcript-model';
import type { ChatUncertaintyMarkerStore } from './chat-uncertainty-marker';

/**
 * The refusal-reason whitelist (`beekeeper-progress-indicator` §1). A `refusal` classifies
 * `failed` (composer re-enabled, resending is safe) only when its `reason` is on this list; every
 * unrecognised reason, and an unparseable/absent reason, classifies `uncertain` instead —
 * "unknown means unknown" is the conservative default.
 *
 * Every entry here was verified against `waggle-server/src/rounds_chat/mod.rs` and
 * `waggle-server/src/rounds_chat/practice.rs` (plus the `PracticeError`/`PracticeCapacity` reason
 * tables in `waggle-server/src/rounds_practice/{mod,admission}.rs`) to run on a path that provably
 * completes **before** `ChatThread::begin_turn` — the first irreversible step, at which point the
 * player's message is already appended to the transcript. `not_admitting` is deliberately
 * EXCLUDED even though one of its two call sites (`sandbox.is_admitting()`) runs before
 * `begin_turn`: the server returns the exact same string from a second call site, after
 * `try_spawn_generation` fails on the practice AND tournament paths, which is strictly after
 * `begin_turn` — so the reason alone cannot tell the two apart, and the whole string is untrusted.
 *
 * `turn_in_progress` is ALSO deliberately excluded (`beekeeper-progress-indicator` review, C2) —
 * for a different reason than `not_admitting`, and one worth spelling out because it used to sit
 * right here. It genuinely IS provable pre-admission for *this* send: `reserve_turn` fails
 * strictly before `begin_turn` on both paths, so as far as "was retrying THIS message safe" goes,
 * treating it as pre-admission was never wrong. What was wrong is that this whitelist answers only
 * that question, and a caller was using membership on it to answer a second, different one —
 * "is nothing running, so is it safe to release the gate" — for which `turn_in_progress` is the
 * worst possible witness: it is the refusal that exists FOR the case where a turn is demonstrably
 * live in the sandbox right now, the exact opposite of what `failed` implies to every other reader
 * of `phase` (the composer, the mutation-withholding gate). Classifying it `failed` reopened
 * Run/version-select/hand-edit while the server itself had just said a turn was still in flight.
 * It is handled explicitly in `handlers().onError` below instead of through this whitelist: the
 * channel stays busy and reconciles by rehydrating immediately (never waiting on the player's own
 * "Refresh conversation" press), so what surfaces is the turn actually blocking them, not a dead
 * error inviting a resend that would just be refused again.
 *
 * - 400 — the extractor/validator refuses before the domain fork even runs:
 *   `invalid_body` (a malformed JSON body), `invalid_message` (empty or over the length cap).
 * - 401/403 — authorization runs before scope resolution, let alone `begin_turn`:
 *   `unauthorized` (tournament), `missing_or_malformed_bearer_token` (a malformed bearer width),
 *   `practice_unauthorized` (a practice credential that does not own the scope).
 * - The practice metered gate (`PracticeAdmission::admit_turn`, plus the `admit_mutation`/
 *   `try_reserve_turn` steps ahead of it) completes entirely before `begin_turn` on the practice
 *   send-message path (`practice.rs`'s own documented ordering) — its every refusal reason:
 *   `practice_expiring`, `practice_code_required`, `practice_identity_cap`,
 *   `practice_mint_rate_limited`, `practice_bytes_server_wide`, `practice_bytes_scope`,
 *   `practice_turns_in_flight`, `practice_spend_exhausted`, `practice_unavailable`.
 */
const PRE_ADMISSION_REFUSAL_REASONS: ReadonlySet<string> = new Set([
  'invalid_body',
  'invalid_message',
  'unauthorized',
  'missing_or_malformed_bearer_token',
  'practice_unauthorized',
  'practice_expiring',
  'practice_code_required',
  'practice_identity_cap',
  'practice_mint_rate_limited',
  'practice_bytes_server_wide',
  'practice_bytes_scope',
  'practice_turns_in_flight',
  'practice_spend_exhausted',
  'practice_unavailable',
]);

/** `turn_in_progress` (`beekeeper-progress-indicator` C2) — see the whitelist's own doc comment
 * above for why this is not simply "one more unrecognised reason". Kept as its own named constant
 * (rather than an inline string literal in `onError`) so the one call site and the one doc comment
 * describing it stay impossible to drift apart. */
const LIVE_TURN_REFUSAL_REASON = 'turn_in_progress';

/** Classifies a send-leg failure observed before any `turnStarted` for that send. `transport`
 * (the connection never definitively answered) and any `refusal` whose reason is not on the
 * whitelist above both land on `uncertain` — the conservative default the whitelist exists to
 * protect. */
function classifySendLegFailure(error: TurnStreamError): Extract<ChatPhase, 'failed' | 'uncertain'> {
  if (error.kind === 'refusal' && error.reason !== undefined && PRE_ADMISSION_REFUSAL_REASONS.has(error.reason)) {
    return 'failed';
  }
  return 'uncertain';
}

/** The turn ids `transcript` already holds — the uncertainty marker's baseline, captured at the
 * moment of a send so a later rehydrate can tell "this submission settled" from "some other
 * turn — another tab's, or one already over before this send — is sitting there". */
function knownTurnIds(transcript: TranscriptState): string[] {
  const ids = new Set<string>();
  for (const item of transcript.items) {
    if (item.kind === 'terminal') {
      ids.add(item.terminal.turnId);
    }
  }
  if (transcript.currentTurnId !== null) {
    ids.add(transcript.currentTurnId);
  }
  return [...ids];
}

/** The most recently appended player message in a thread, or `null` if it holds none (review,
 * P1-D). The wire carries no per-message `turnId` (`transcript-model.ts`'s own note: "the wire
 * carries no per-message turnId"), so this is the closest a client-only read gets to "the message
 * that opened the newest turn" — the freshest player line, whichever turn it belongs to. */
function lastPlayerMessageText(dto: ThreadDto): string | null {
  for (let index = dto.messages.length - 1; index >= 0; index -= 1) {
    const message = dto.messages[index]!;
    if (message.role === 'player') {
      return message.text;
    }
  }
  return null;
}

/** Whether the thread carries any agent-authored message after the player's most recent line
 * (`beekeeper-progress-indicator` H1). Walking from the end and stopping at the first message of
 * either role answers it in one pass: hitting an `agent` message first means the turn that follows
 * the player's own line said something; hitting the `player` message first (or running out of
 * messages) means nothing did. This is the server's `not_admitting` post-write path made visible —
 * `begin_turn` appended the player's message, the turn then settled `Stopped` with an empty log,
 * and the thread never grew an agent reply for it at all. Like `lastPlayerMessageText` this cannot
 * be exact (the wire carries no per-message `turnId`), but it answers the question this resolution
 * actually needs: did *anything* come back, or is the player looking at their own line in a void. */
function settledTurnProducedOutput(dto: ThreadDto): boolean {
  for (let index = dto.messages.length - 1; index >= 0; index -= 1) {
    const message = dto.messages[index]!;
    if (message.role === 'agent') {
      return true;
    }
    if (message.role === 'player') {
      return false;
    }
  }
  return false;
}

/** The subset of `ChatSseClient`/`RoundsHttpClient` this channel needs — both satisfy it
 * structurally. */
export interface ChatTransport {
  sendMessage(
    text: string,
    targetType: string | null,
    handlers: TurnStreamHandlers,
  ): { close(): void };
  attachTurn(turnId: string, afterCursor: number, handlers: TurnStreamHandlers): { close(): void };
  stopTurn(turnId: string): Promise<boolean>;
  fetchThread(): Promise<ThreadDto | null>;
  /** `DELETE /chat/thread` — deletes the conversation server-side (WAG-94). **Optional**: a
   * read-only transport (the presentation preview, which the server mounts only the GET routes for)
   * simply omits it, and the channel then exposes no `clearConversation` for a renderer to offer.
   * `false` on any failure, exactly as `stopTurn` reports one. */
  clearThread?(): Promise<boolean>;
  /** The editor's re-read after a turn cuts a version — `RoundsHttpClient.fetchColony`. */
  fetchColony(): Promise<ColonyMeta | null>;
}

/** The store-facing half — mirrors `rounds-prompt.ts`'s own `RoundsPromptStoreSeam` precedent
 * (a structural seam rather than the store itself, so this module stays unit-testable). */
export interface ChatChannelStoreSeam {
  applyColonyMeta(meta: ColonyMeta): void;
}

const RETRY_DELAY_MS = 1_000;

export interface ChatChannelDeps {
  transport: ChatTransport;
  /** Where an accepted turn's re-read colony lands. Defaults to a no-op seam so a test that does
   * not care about this can omit it — production ALWAYS supplies the real store. */
  store?: ChatChannelStoreSeam;
  /** Timer seam for the dropped-connection reattach backoff. Defaults to `setTimeout`. */
  scheduler?: Scheduler;
  /** The scope-keyed `uncertain`-phase persistence (`beekeeper-progress-indicator` §1). Defaults
   * to a no-op store (nothing survives a reload) so a caller that does not care about this — a
   * demo/ephemeral chat channel, most tests — can omit it; production always supplies a real one,
   * scoped to this channel's own sandbox via `createChatUncertaintyMarkerStore`. */
  uncertaintyMarker?: ChatUncertaintyMarkerStore;
}

const NOOP_STORE: ChatChannelStoreSeam = { applyColonyMeta: () => {} };
const NOOP_MARKER_STORE: ChatUncertaintyMarkerStore = {
  get: () => null,
  set: () => {},
  clear: () => {},
};

function wallClockScheduler(): Scheduler {
  return {
    setTimer: (fn, ms) => setTimeout(fn, ms) as unknown as number,
    clearTimer: (id) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>),
  };
}

export function createChatChannel(deps: ChatChannelDeps): ChatSeam {
  const { transport } = deps;
  const store = deps.store ?? NOOP_STORE;
  const scheduler = deps.scheduler ?? wallClockScheduler();
  const uncertaintyMarker = deps.uncertaintyMarker ?? NOOP_MARKER_STORE;

  // C1 (`beekeeper-progress-indicator` review): read synchronously, at construction — never only
  // inside the async `rehydrate()` below. `busy` must never be false while the channel does not
  // yet know what is happening, and a marker on disk IS knowledge: it means the LAST session ended
  // with an outcome still unresolved. Deferring this read to `rehydrate()`'s eventual network
  // response left a full fetch round-trip, right after every reload, during which the synchronous
  // default was `idle` — composer enabled, mutation gate open — which is exactly the duplicate-send
  // window the marker exists to close. `view: 'open'` mirrors the same rule `rehydrate()` itself uses
  // when it re-confirms `uncertain` from a marker (`publish({ ...snapshot, view: 'open', phase:
  // 'uncertain', ... })` below): there is nothing to gate on "was rehydrate() called yet" — the
  // player should see the uncertain state the moment the channel exists.
  const initialMarker = uncertaintyMarker.get();
  let snapshot: ChatSeamSnapshot =
    initialMarker !== null
      ? { view: 'open', unread: false, transcript: EMPTY_TRANSCRIPT, phase: 'uncertain', awaitingFirstOutput: false }
      : { view: 'closed', unread: false, transcript: EMPTY_TRANSCRIPT, phase: 'idle', awaitingFirstOutput: false };
  const listeners = new Set<() => void>();
  let activeStream: { close(): void } | null = null;
  let retryTimer: number | null = null;
  let playerMessageCounter = 0;

  /** Monotonic attempt token (review, P1-A/P1-B). Bumped every time the channel begins a fresh
   * attempt at anything asynchronous — a send, a rehydration, or a (re)attach. Every async
   * continuation and every stream callback closes over the token that was current when IT began,
   * and checks that token against the CURRENT value before publishing anything: a result from an
   * attempt the channel has since moved past (a newer send raced ahead of a stale rehydrate; a
   * newer rehydrate superseded an older one still awaiting the network; a dropped stream's queued
   * callback arrives after a reattach already replaced it) is discarded rather than clobbering
   * whatever the live attempt has already published. */
  let attemptToken = 0;

  function beginAttempt(): number {
    attemptToken += 1;
    return attemptToken;
  }

  function publish(next: ChatSeamSnapshot): void {
    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function stopRetryTimer(): void {
    if (retryTimer !== null) {
      scheduler.clearTimer(retryTimer);
      retryTimer = null;
    }
  }

  function closeStream(): void {
    activeStream?.close();
    activeStream = null;
    stopRetryTimer();
  }

  function scheduleReattach(): void {
    if (retryTimer !== null) {
      return;
    }
    retryTimer = scheduler.setTimer(() => {
      retryTimer = null;
      const turnId = snapshot.transcript.currentTurnId;
      if (turnId === null || !snapshot.transcript.running) {
        return; // the turn settled (or a fresh one started) before the retry fired
      }
      const cursor = snapshot.transcript.cursorByTurn[turnId] ?? 0;
      const token = beginAttempt();
      activeStream = transport.attachTurn(turnId, cursor, handlers(token));
    }, RETRY_DELAY_MS);
  }

  function handlers(token: number): TurnStreamHandlers {
    return {
      onEvent: (event) => {
        if (token !== attemptToken) {
          return; // a stale/superseded attempt — the channel has since moved on
        }
        const transcript = applyTurnEvent(snapshot.transcript, event);
        let phase: ChatPhase = snapshot.phase;
        let awaitingFirstOutput = snapshot.awaitingFirstOutput;
        if (event.kind === 'turnStarted') {
          phase = 'running';
          // Admission is now a known fact for OUR OWN live stream (review, P1-C) — clear
          // immediately rather than waiting for `turnFinished`, so a reload between now and
          // settlement does not resurrect `uncertain` for an outcome that is no longer in doubt.
          uncertaintyMarker.clear();
        } else if (event.kind === 'textDelta' || event.kind === 'assistantMessage' || event.kind === 'toolStarted') {
          awaitingFirstOutput = false;
        } else if (event.kind === 'turnFinished') {
          phase = 'idle';
          awaitingFirstOutput = false;
          uncertaintyMarker.clear();
        }
        // **The unread halo** (`player-chat-and-outcome` **D-3**). Latched here, on arrival, and
        // released only by the player opening the thread — so it is a claim about what they have
        // seen, not about how long ago something happened. `turnStarted` is excluded deliberately:
        // it is the channel's own echo of the send the player just made, not something the agent
        // said back. Everything else — text, a tool row, the terminal — is content that landed
        // behind a panel that was not on screen.
        const unread = snapshot.unread || (snapshot.view !== 'open' && event.kind !== 'turnStarted');
        publish({ ...snapshot, transcript, phase, awaitingFirstOutput, unread });
        if (event.kind === 'turnFinished' && event.version !== null && event.version !== undefined) {
          void transport.fetchColony().then((meta) => {
            if (meta !== null) {
              store.applyColonyMeta(meta);
            }
          });
        }
      },
      onDone: () => {
        if (token !== attemptToken) {
          return; // a stale/superseded attempt — nothing to reconcile
        }
        // The server closes the stream once the terminal event has been sent — nothing further
        // to do here; `turnFinished` already flipped `running` false via `onEvent` above. If the
        // stream closed WITHOUT a terminal event ever arriving (a hard cut), `running` is still
        // true and looks exactly like a drop, so the same reattach path below covers it too.
        activeStream = null;
        if (snapshot.transcript.running) {
          scheduleReattach();
          return;
        }
        // Review, P1-B: a clean EOF that arrives while still `submitting` — the stream closed
        // with no `turnStarted` ever observed for this send — is NOT a silent stall to leave the
        // channel stuck in forever. It is exactly as ambiguous as a `transport` error at the same
        // point: the server may or may not have admitted the turn. Any other phase here means the
        // outcome already resolved through some other path (a `turnFinished` already seen, an
        // earlier `onError` already classified it) — this EOF is just the connection's own
        // trailing close and needs no further action.
        if (snapshot.phase === 'submitting') {
          publish({ ...snapshot, phase: 'uncertain', awaitingFirstOutput: false });
        }
      },
      onError: (error) => {
        if (token !== attemptToken) {
          return; // a stale/superseded attempt — nothing to reconcile
        }
        activeStream = null;
        if (snapshot.transcript.running) {
          // A reattach-leg (or a send-leg failure arriving after `turnStarted`) failure — today's
          // unchanged recovery. This is never the ambiguous case: a `turnStarted` already means
          // admission is a known fact, so there is nothing here for the whitelist to classify.
          scheduleReattach();
          return;
        }
        // Review, P1-B: a LATE `onError` can still arrive after the turn already settled through
        // this very same stream (`turnFinished` already moved the phase off `submitting`, e.g. to
        // `idle`). `snapshot.transcript.running` is `false` in that case too, which used to fall
        // straight into the send-leg classifier below and flip an already-successful turn to
        // `uncertain`. Only a callback that arrives WHILE we are still `submitting` — meaning no
        // `turnStarted` for this send has been observed yet — is actually the ambiguous send-leg
        // failure the classifier exists for; anything else here is a stale echo of an outcome
        // that is already known and must be left alone.
        if (snapshot.phase !== 'submitting') {
          return;
        }
        // C2 (`beekeeper-progress-indicator` review): `turn_in_progress` is handled BEFORE the
        // ordinary whitelist classifier, not folded into it — see `LIVE_TURN_REFUSAL_REASON`'s own
        // doc comment for why membership on the pre-admission whitelist is the wrong question here.
        // This refusal is positive evidence a turn is live RIGHT NOW, so `failed` (which every
        // other reader of `phase` — the composer, the mutation gate — takes as "nothing is
        // happening, safe to release") would be a lie. The channel stays busy and reconciles
        // immediately by rehydrating rather than waiting on the player's own "Refresh conversation"
        // press, so what surfaces is the turn actually blocking them, not a dead error.
        if (error.kind === 'refusal' && error.reason === LIVE_TURN_REFUSAL_REASON) {
          // Our OWN message provably was not admitted (`reserve_turn` fails before `begin_turn`),
          // so the marker's job — correlating a submission whose fate is unknown — is moot: our
          // fate IS known (not admitted). Clearing it means the rehydrate below takes the ordinary
          // no-marker path, which attaches to whatever the thread reports in flight without
          // requiring a text match against OUR submission — exactly right, since the turn blocking
          // us is demonstrably someone/something else's, never ours.
          uncertaintyMarker.clear();
          publish({ ...snapshot, phase: 'uncertain', awaitingFirstOutput: false });
          void rehydrate();
          return;
        }
        // A send-leg failure BEFORE any `turnStarted` for this send (`sendAndOpen`'s own guard
        // means this can only be the `submitting` phase — a second send while busy is refused
        // before it ever reaches the transport). Classified by evidence, never auto-recovered:
        // no `scheduleReattach` here — the player's only affordance out of `uncertain` is the
        // explicit, on-demand `rehydrate()` ("Refresh conversation"), never an automatic retry
        // that could invite a duplicate send.
        const phase = classifySendLegFailure(error);
        if (phase === 'failed') {
          // A demonstrably pre-admission refusal: nothing was admitted, so the marker this send
          // set has nothing left to protect against — resending is the retry mechanism.
          uncertaintyMarker.clear();
        }
        publish({ ...snapshot, phase, awaitingFirstOutput: false });
      },
    };
  }

  // A named function declaration (hoisted), not an arrow assigned inline in the returned object
  // literal below: C2's `turn_in_progress` handling in `handlers().onError` above needs to call
  // this itself (`void rehydrate()`), and a plain declaration is the simplest way to give both that
  // closure and the returned `ChatSeam` the exact same function without threading a second
  // reference through `ChatChannelDeps` or reassigning a `let`.
  async function rehydrate(): Promise<void> {
    // Boot AND every on-demand "Refresh conversation" press while `uncertain` go through here —
    // there is exactly one rehydration path (`beekeeper-progress-indicator` §1), so the marker
    // resolution rules apply uniformly to both. C2 adds a THIRD caller: `handlers().onError`'s own
    // `turn_in_progress` branch, reconciling automatically rather than waiting on the player.
    //
    // Review, P1-A: this attempt is stamped with its own token BEFORE the network await below.
    // A `sendAndOpen` (or a newer, overlapping `rehydrate()`) that races ahead while this await
    // is in flight bumps `attemptToken` past this call's `token` — so when this await resolves,
    // the check just below tells a stale result apart from the live one and discards it instead
    // of clobbering whatever the newer attempt already published (an optimistic send transcript,
    // or a newer rehydrate's own conclusion).
    const token = beginAttempt();
    const marker = uncertaintyMarker.get();
    const dto = await transport.fetchThread();
    if (token !== attemptToken) {
      return; // superseded while this fetch was in flight — the result is stale, discard it
    }
    if (dto === null) {
      // Total transport failure — no evidence either way. A marker's demand is retained
      // explicitly: a fresh/failed channel otherwise defaults to `idle`, which would re-enable
      // the composer and reopen the very duplicate-send hole the marker exists to close. With
      // no marker, this is the pre-existing "inert default" behaviour, unchanged.
      if (marker !== null) {
        publish({ ...snapshot, view: 'open', phase: 'uncertain', awaitingFirstOutput: false });
      }
      return;
    }
    const transcript = transcriptFromThread(dto);
    if (marker !== null) {
      // Review, P1-D: the baseline alone narrows "which turn is this" but is not proof — another
      // tab could have started a turn while our own POST was still racing toward admission.
      // Requiring the observed turn's player message to match the text WE submitted narrows the
      // window further. It still is not proof: nothing the server returns correlates an
      // observed turn to our specific request, so two tabs racing the exact same text within the
      // same ambiguity window remain indistinguishable. That gap is real and is NOT closed here
      // — the complete fix is a server-issued correlation/idempotency key (the descoped design
      // preserved in this change's `reviews/`); no further client-side heuristics belong on top
      // of this one (`beekeeper-progress-indicator` §1).
      const textMatches = lastPlayerMessageText(dto) === marker.submittedText;
      // An in-flight turn NOT already in the baseline is evidence THIS submission was admitted
      // and is running — one already in the baseline was already known about before the send
      // and resolves nothing (another tab's turn, or one that predates it).
      if (textMatches && dto.inFlight !== null && !marker.baseline.includes(dto.inFlight.turnId)) {
        const inFlight = dto.inFlight;
        uncertaintyMarker.clear();
        publish({ ...snapshot, view: 'open', unread: false, transcript, phase: 'running', awaitingFirstOutput: true });
        closeStream();
        const attachToken = beginAttempt();
        activeStream = transport.attachTurn(
          inFlight.turnId,
          transcript.cursorByTurn[inFlight.turnId] ?? 0,
          handlers(attachToken),
        );
        return;
      }
      // A settled turn not already in the baseline is evidence this submission completed while
      // this tab was away.
      const newlySettled = textMatches
        ? dto.turns.find((turn) => !marker.baseline.includes(turn.turnId))
        : undefined;
      if (newlySettled !== undefined) {
        uncertaintyMarker.clear();
        // H1 (`beekeeper-progress-indicator` review): the server's post-write `not_admitting` path
        // can settle a turn `Stopped` with an EMPTY log after `begin_turn` has already appended the
        // player's message. The gate handling above is already correct either way — the turn
        // genuinely settled, nothing holds the sandbox — but silently landing in `idle` leaves the
        // player looking at their own message with no reply, no error, and no explanation. The
        // outcome IS known here (unlike `uncertain`), so `failed` is the right phase: it already
        // re-enables the composer, and resending is exactly the correct recovery for "that didn't
        // reach the Beekeeper". No sixth phase — this is `failed` with an honest reason attached.
        if (settledTurnProducedOutput(dto)) {
          publish({ ...snapshot, view: 'closed', transcript, phase: 'idle', awaitingFirstOutput: false });
        } else {
          publish({
            ...snapshot,
            view: 'open',
            unread: false,
            transcript,
            phase: 'failed',
            awaitingFirstOutput: false,
            noOutputSettle: true,
          });
        }
        return;
      }
      // Neither (or the text didn't match): no evidence yet. Restore/retain `uncertain` — still
      // busy, gate held, composer locked — resolved only by a later, evidence-bearing refresh.
      // **There is no time-based release**: an empty read here is not proof of non-admission,
      // since the original POST may still be racing toward admission when this fetch returned.
      publish({ ...snapshot, view: 'open', transcript, phase: 'uncertain', awaitingFirstOutput: false });
      return;
    }
    // No marker: the ordinary rehydrate path, unchanged in behaviour beyond also stating the
    // phase. A turn reattached here may already have emitted output before the reload — the
    // replay below runs through the same `onEvent` handler live events do, so
    // `awaitingFirstOutput` clears itself the moment a replayed `textDelta`/`assistantMessage`/
    // `toolStarted` arrives, exactly as it would for a genuinely live one.
    publish({
      ...snapshot,
      view: transcript.running ? 'open' : 'closed',
      unread: false,
      transcript,
      phase: transcript.running ? 'running' : 'idle',
      awaitingFirstOutput: transcript.running,
    });
    if (transcript.running && transcript.currentTurnId !== null) {
      closeStream();
      const attachToken = beginAttempt();
      activeStream = transport.attachTurn(
        transcript.currentTurnId,
        transcript.cursorByTurn[transcript.currentTurnId] ?? 0,
        handlers(attachToken),
      );
    }
  }

  /** "New chat" (WAG-94). Present only when the transport can actually delete the thread — see
   * `ChatSeam.clearConversation` for why absence, rather than a no-op, is what a read-only
   * transport produces.
   *
   * **The delete leads and the local reset follows.** Clearing locally first would show an empty
   * conversation the server still holds, and a reload would bring it back — the one thing "start a
   * new one" cannot mean. A refused or failed delete therefore leaves the transcript exactly where
   * it was and rehydrates, so what the player sees is what the server has. */
  async function clearConversation(): Promise<void> {
    if (isChatBusy(snapshot.phase) || snapshot.transcript.running) {
      return;
    }
    // Stamped before the network await, exactly as `rehydrate()` is and for the same reason
    // (review, P1-A): a send pressed while the DELETE is still in flight bumps the token past this
    // one, and its optimistic transcript must not then be wiped by this call's own reset.
    const token = beginAttempt();
    const cleared = (await deps.transport.clearThread?.()) ?? false;
    if (token !== attemptToken) {
      return; // superseded — whatever raced ahead owns the channel's state now
    }
    if (!cleared) {
      // Refused (a turn raced in), or the request never landed. Either way the server still holds
      // the conversation, so the honest answer is what the server has — never a local empty one.
      await rehydrate();
      return;
    }
    closeStream();
    // The marker correlates a submission whose fate was unknown against a thread that no longer
    // exists; nothing it could ever match survives the clear, so it goes with the conversation.
    uncertaintyMarker.clear();
    playerMessageCounter = 0;
    // `view: 'open'` rather than `'closed'`: WAG-94's clear starts a *fresh* conversation in the
    // panel the player is looking at — it ends a thread, it does not put the overlay away. And
    // `unread: false` because clearing is the player's own act, performed with the thread in front
    // of them; there is by construction nothing unseen in a transcript that is now empty.
    publish({
      view: 'open',
      unread: false,
      transcript: EMPTY_TRANSCRIPT,
      phase: 'idle',
      awaitingFirstOutput: false,
    });
  }

  return {
    getSnapshot: () => snapshot,
    ...(transport.clearThread === undefined ? {} : { clearConversation }),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    sendAndOpen: (text, targetType) => {
      if (isChatBusy(snapshot.phase)) {
        // The double-send bug this guard exists to close (`beekeeper-progress-indicator` §1): a
        // second press must not tear down the live turn's stream by calling `closeStream()`
        // before posting a request the server would refuse anyway.
        return;
      }
      // The marker's baseline is captured from the CURRENT transcript — before the player's own
      // message is appended — so a reload that lands back here can tell this submission's turn
      // apart from anything the thread already held. `submittedText` is the exact text just sent
      // (review, P1-D) — the text-match a later `rehydrate()` requires before treating an observed
      // turn as evidence about THIS submission, not just an unrelated one the baseline missed.
      uncertaintyMarker.set({ baseline: knownTurnIds(snapshot.transcript), submittedText: text });
      closeStream();
      playerMessageCounter += 1;
      const withPlayerMessage = appendPlayerMessage(snapshot.transcript, `player-${playerMessageCounter}`, text);
      publish({ ...snapshot, view: 'open', unread: false, transcript: withPlayerMessage, phase: 'submitting', awaitingFirstOutput: true });
      const token = beginAttempt(); // invalidates any rehydrate() still awaiting the network (P1-A)
      activeStream = transport.sendMessage(text, targetType, handlers(token));
    },
    openExisting: () => {
      // Opening IS the read (`player-chat-and-outcome` **D-3**) — the halo clears here and nowhere
      // else, so it always means "unread", never "recent".
      publish({ ...snapshot, view: 'open', unread: false });
    },
    stop: () => {
      const turnId = snapshot.transcript.currentTurnId;
      if (turnId === null || !snapshot.transcript.running) {
        return;
      }
      void transport.stopTurn(turnId);
    },
    // **D-2.** Deliberately the whole implementation: no `closeStream()`, no `stopTurn`, no
    // `stopRetryTimer()`, no marker touch. A minimise that reached any of those would be the bug
    // this feature exists to avoid — the live turn goes on writing into the transcript behind the
    // dismissed panel, and `ChatReentryRow` reports it. Unconditional, so it is honoured in exactly
    // the phase `close()` refuses.
    minimise: () => {
      publish({ ...snapshot, view: 'minimised' });
    },
    close: () => {
      if (snapshot.transcript.running) {
        return;
      }
      publish({ ...snapshot, view: 'closed' });
    },
    rehydrate,
  };
}
